import { Model as BaseModel } from "vue-api-query";

export default class Model extends BaseModel {
	constructor( ...args ) {
		super( ...args );

		if ( typeof this.singularNoun === "undefined" ) {
			throw new Error( "You must declare singularNoun getter." );
		}

		if ( typeof this._builder === "undefined" ) {
			this.applyMutations( this );

			this.$ = JSON.parse( JSON.stringify( this ) );
		}
	}

	get pluralNoun() {
		return `${this.singularNoun}s`;
	}

	resource() {
		return this.pluralNoun;
	}

	baseURL() {
		return "";
	}

	getSaveData() {
		const clone = new this.constructor( JSON.parse( JSON.stringify( this ) ) );

		this.applyMutations( clone );

		Object.keys( this.mutations || {} ).forEach( propKey => {
			if ( !clone[propKey] ) {
				return;
			}

			if ( Array.isArray( clone[propKey] ) ) {
				const primaryKeyArr = [];

				clone[propKey].forEach( item => {
					if ( typeof item.getPrimaryKey === "function" ) {
						primaryKeyArr.push( item.getPrimaryKey() );
					}
				} );

				clone[propKey] = primaryKeyArr;
			} else if ( typeof clone[propKey].getPrimaryKey === "function" ) {
				clone[propKey] = clone[propKey].getPrimaryKey();
			}
		} );

		delete clone.$;

		return clone;
	}

	_create() {
		return this.request( {
			method: "POST",
			url: this.endpoint(),
			data: this.getSaveData()
		} )
		.then( response => Object.assign( this, response.data ) );
	}

	_update() {
		return this.request( {
			method: "PUT",
			url: this.endpoint(),
			data: this.getSaveData()
		} )
		.then( response => Object.assign( this, response.data ) );
	}

	async find( id ) {
		BaseModel.app.$wait.start( `find ${this.singularNoun}` );

		try {
			return await super.find( id );
		} finally {
			BaseModel.app.$wait.end( `find ${this.singularNoun}` );
		}
	}

	async findPageModel( id ) {
		try {
			return await this.find( id );
		} catch ( error ) {
			if ( error.response.status === 404 ) {
				BaseModel.app.$router.replace( { name: "notFound" } );
			}
		}
	}

	static findPageModel( id ) {
		return this.instance().findPageModel( id );
	}

	async get() {
		BaseModel.app.$wait.start( `get ${this.pluralNoun}` );

		try {
			return await super.get();
		} finally {
			BaseModel.app.$wait.end( `get ${this.pluralNoun}` );
		}
	}

	async delete() {
		BaseModel.app.$wait.start( `delete ${this.singularNoun}` );

		try {
			return await super.delete().then( () => this.showDeletedMessage() );
		} finally {
			BaseModel.app.$wait.end( `delete ${this.singularNoun}` );
		}
	}

	showDeletedMessage() {
		const noun = `${this.singularNoun.charAt( 0 ).toUpperCase()}${this.singularNoun.substr( 1 )}`;

		return BaseModel.app.$store.commit( "toasts/SHOW", {
			color: "success",
			text: `${noun} deleted`
		} );
	}

	static showDeletedMessage() {
		return this.instance().showDeletedMessage();
	}

	async save( showSuccessMessage = true ) {
		BaseModel.app.$wait.start( `save ${this.singularNoun}` );

		try {
			return await super.save().then( model => {
				model.$ = JSON.parse( JSON.stringify( model ) );

				if ( showSuccessMessage ) {
					BaseModel.app.$store.commit( "toasts/SHOW", {
						color: "success",
						text: "Successfully saved"
					} );
				}

				this.applyMutations( model );

				return model;
			} );
		} catch ( e ) {
			let errorMessage = "There was an error when saving. You can try again in a moment or try refreshing the page.";

			this.applySavedState( this );

			if ( e
				&& e.response
				&& e.response.data
				&& e.response.data.message
			) {
				errorMessage = e.response.data.message;
			}

			BaseModel.app.$store.commit( "toasts/SHOW", {
				color: "error",
				text: errorMessage
			} );
		} finally {
			BaseModel.app.$wait.end( `save ${this.singularNoun}` );
		}
	}

	async count() {
		const base = this._customResource
			|| this._fromResource
			|| `${this.baseURL()}/${this.resource()}`;

		BaseModel.app.$wait.start( `count ${this.pluralNoun}` );

		try {
			return await this.request( {
				method: "HEAD",
				url: `${base}${this._builder.query()}`
			} )
			.then( ( { headers } ) =>
				( headers["x-total-count"]
					? parseInt( headers["x-total-count"] )
					: null
				)
			);
		} finally {
			BaseModel.app.$wait.end( `count ${this.pluralNoun}` );
		}
	}

	static count() {
		return this.instance().count();
	}

	applySavedState( model ) {
		if ( typeof model.$ !== "undefined" ) {
			Object.keys( model.$ ).forEach( attrName => {
				model[attrName] = model.$[attrName];
			} );

			this.applyMutations( model );
		}
	}

	applyMutations( model ) {
		Object.keys( model.mutations || {} ).forEach( propKey => {
			if ( !model[propKey] ) {
				return;
			}

			const ModelForKey = model.mutations[propKey];

			if ( Array.isArray( model[propKey] ) ) {
				model[propKey].forEach( ( item, i ) => {
					const modelData = model[propKey][i];

					model[propKey][i] = new ModelForKey( modelData );
					model[propKey][i].$ = JSON.parse( JSON.stringify( modelData ) );

					this.applyMutations( model[propKey][i] );
				} );
			} else {
				const modelData = model[propKey];

				model[propKey] = new ModelForKey( modelData );
				model[propKey].$ = JSON.parse( JSON.stringify( modelData ) );

				this.applyMutations( model[propKey] );
			}
		} );
	}

	request( config ) {
		return this.$http.request( config );
	}
}
